// src/pages/SurahPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Heading, Text, SimpleGrid, Card, CardBody, Input, InputGroup, InputLeftElement,
  ButtonGroup, Button, HStack, Badge, Spinner, Alert, AlertIcon, Select, useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Search, Play, Pause, Download, ChevronLeft, ChevronRight, List } from 'lucide-react';
import QuranShell from '../components/Quran/QuranShell';
import ReaderSettings from '../components/Quran/ReaderSettings';
import AyahCard, { BISMILLAH } from '../components/Quran/AyahCard';
import useQuranSettings from '../hooks/useQuranSettings';
import useSurahList from '../hooks/useSurahList';
import useRecitations from '../hooks/useRecitations';
import useAyahPlayer from '../hooks/useAyahPlayer';
import { fetchSurah, downloadFile } from '../api/quranService';
import { getSurah } from '../data/surahMeta';

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'meccan', label: 'Meccan' },
  { key: 'medinan', label: 'Medinan' },
];


// ============ SURAH INDEX ============

const SurahCard = ({ surah }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const numberBg = useColorModeValue('teal.50', 'teal.900');
  return (
    <Card
      as={RouterLink} to={`/surahs/${surah.id}`}
      bg={cardBg} variant="outline" borderColor={borderColor} borderRadius="xl"
      transition="all 0.15s" _hover={{ borderColor: 'teal.400', boxShadow: 'md' }}
    >
      <CardBody py={4}>
        <Flex align="center" gap={4}>
          <Flex
            w="40px" h="40px" flexShrink={0} align="center" justify="center" borderRadius="lg"
            bg={numberBg} color="teal.600" fontWeight="bold" fontSize="sm" transform="rotate(45deg)"
          >
            <Box transform="rotate(-45deg)">{surah.id}</Box>
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontWeight="semibold" noOfLines={1}>{surah.name}</Text>
            <Text fontSize="xs" color={subtextColor} noOfLines={1}>
              {surah.ename || ' '}
            </Text>
          </Box>
          <Box textAlign="right" flexShrink={0}>
            <Text fontFamily="'Amiri Quran', serif" fontSize="lg" lineHeight="1.4">{surah.arabicName}</Text>
            <Text fontSize="xs" color={subtextColor}>{surah.ayahCount} ayahs</Text>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

const SurahIndex = () => {
  const surahs = useSurahList();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const inputBg = useColorModeValue('white', 'gray.800');

  const term = search.trim().toLowerCase();
  const shown = surahs.filter((s) => {
    const matches = !term || s.name.toLowerCase().includes(term) || s.ename.toLowerCase().includes(term)
      || s.arabicName.includes(search.trim()) || String(s.id) === term;
    return matches && (type === 'all' || s.type.toLowerCase() === type);
  });

  return (
    <>
      <Box mb={6}>
        <Heading size="lg" mb={1}>Surahs</Heading>
        <Text color="gray.500">Read and listen to all 114 surahs, with translation and recitation.</Text>
      </Box>
      <Flex gap={3} mb={6} direction={{ base: 'column', md: 'row' }}>
        <InputGroup>
          <InputLeftElement pointerEvents="none"><Search size={18} color="gray" /></InputLeftElement>
          <Input bg={inputBg} placeholder="Search by name, meaning or number" value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>
        <ButtonGroup size="md" isAttached variant="outline" colorScheme="teal">
          {TYPE_FILTERS.map((f) => (
            <Button key={f.key} variant={type === f.key ? 'solid' : 'outline'} onClick={() => setType(f.key)}>{f.label}</Button>
          ))}
        </ButtonGroup>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
        {shown.map((s) => <SurahCard key={s.id} surah={s} />)}
      </SimpleGrid>
      {shown.length === 0 && <Text color="gray.500" textAlign="center" py={10}>No surah matches your search.</Text>}
    </>
  );
};


// ============ SURAH READER ============

const SurahReader = ({ surahId, player }) => {
  const navigate = useNavigate();
  const settings = useQuranSettings();
  const surahs = useSurahList();
  const [ayahs, setAyahs] = useState(null);
  const [error, setError] = useState(null);
  const headerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const surah = surahs.find((s) => s.id === surahId) || { ...getSurah(surahId), ename: '', type: '' };
  const recordings = useRecitations([String(surahId)], settings.reciterId);
  const { stop } = player;

  useEffect(() => {
    let cancelled = false;
    setAyahs(null);
    setError(null);
    fetchSurah(surahId, settings.translationId)
      .then((data) => !cancelled && setAyahs(data.ayahs || []))
      .catch(() => !cancelled && setError('This surah could not be loaded.'));
    return () => { cancelled = true; };
  }, [surahId, settings.translationId]);

  // A new surah or reciter means a new set of recordings
  useEffect(() => stop, [surahId, settings.reciterId, stop]);

  const queue = useMemo(() => (ayahs || [])
    .filter((a) => recordings.ayahs[a.ayah_key])
    .map((a) => ({
      id: a.ayah_key,
      label: `${surah.name} · Ayah ${a.ayah_index}`,
      sublabel: settings.reciterName,
      mediaKey: recordings.ayahs[a.ayah_key].mediaKey,
    })), [ayahs, recordings, surah.name, settings.reciterName]);

  const whole = recordings.whole[String(surahId)];
  const wholeItem = whole && {
    id: `surah-${surahId}`, label: `${surah.name} · Full surah`, sublabel: settings.reciterName, mediaKey: whole.mediaKey,
  };
  const playingAll = wholeItem ? player.isPlaying(wholeItem.id) : queue.some((q) => player.isPlaying(q.id));
  const fileName = (suffix) => `${String(surahId).padStart(3, '0')}_${surah.name}_${suffix}_${settings.reciterName}.mp3`.replace(/\s+/g, '_');

  const playAll = () => {
    if (playingAll) player.toggle();
    else player.playFrom(wholeItem ? [wholeItem] : queue, 0);
  };

  return (
    <>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Button as={RouterLink} to="/surahs" size="sm" variant="ghost" leftIcon={<List size={16} />}>All surahs</Button>
        <Select size="sm" maxW="260px" value={surahId} aria-label="Go to surah"
          onChange={(e) => navigate(`/surahs/${e.target.value}`)}>
          {surahs.map((s) => <option key={s.id} value={s.id}>{s.id}. {s.name}</option>)}
        </Select>
      </Flex>

      {/* Surah header */}
      <Box bg={headerBg} borderWidth="1px" borderColor={borderColor} borderRadius="2xl" p={{ base: 5, md: 8 }} mb={6} textAlign="center">
        <Text fontFamily="'Amiri Quran', serif" fontSize={{ base: '3xl', md: '4xl' }} lineHeight="1.6">{surah.arabicName}</Text>
        <Heading size="lg" mt={1}>{surah.id}. {surah.name}</Heading>
        {surah.ename && <Text color="gray.500">{surah.ename}</Text>}
        <HStack justify="center" spacing={2} mt={3}>
          {surah.type && <Badge colorScheme={surah.type === 'Meccan' ? 'orange' : 'blue'}>{surah.type}</Badge>}
          <Badge>{surah.ayahCount} ayahs</Badge>
        </HStack>
        <HStack justify="center" spacing={3} mt={5} flexWrap="wrap" rowGap={2}>
          <Button
            colorScheme="teal" leftIcon={playingAll ? <Pause size={16} /> : <Play size={16} />}
            onClick={playAll} isDisabled={recordings.loading || (!wholeItem && queue.length === 0)}
            isLoading={recordings.loading} loadingText="Loading audio"
          >
            {playingAll ? 'Pause' : 'Play surah'}
          </Button>
          {wholeItem && (
            <Button variant="outline" colorScheme="teal" leftIcon={<Download size={16} />}
              onClick={() => downloadFile(wholeItem.mediaKey, fileName('full'))}>
              Download surah
            </Button>
          )}
        </HStack>
        {!recordings.loading && !wholeItem && queue.length === 0 && (
          <Text fontSize="sm" color="gray.500" mt={3}>No recitation by {settings.reciterName} for this surah yet.</Text>
        )}
      </Box>

      <ReaderSettings settings={settings} />

      {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
      {!ayahs && !error && <Flex justify="center" py={16}><Spinner size="xl" color="teal.500" /></Flex>}

      {ayahs && (
        <Box>
          {surahId !== 1 && surahId !== 9 && (
            <Text textAlign="center" fontFamily={settings.fontFamily} fontSize="3xl" py={4} dir="rtl">{BISMILLAH}</Text>
          )}
          {ayahs.map((ayah) => (
            <AyahCard
              key={ayah.ayah_key}
              ayah={ayah}
              settings={settings}
              recording={recordings.ayahs[ayah.ayah_key]}
              isActive={player.isActive(ayah.ayah_key)}
              isPlaying={player.isPlaying(ayah.ayah_key)}
              onPlay={() => player.playFrom(queue, queue.findIndex((q) => q.id === ayah.ayah_key))}
              downloadName={fileName(`ayah${ayah.ayah_index}`)}
            />
          ))}
        </Box>
      )}

      {/* Previous / next surah */}
      <Flex justify="space-between" mt={8} gap={3}>
        {surahId > 1 ? (
          <Button as={RouterLink} to={`/surahs/${surahId - 1}`} variant="outline" leftIcon={<ChevronLeft size={16} />}>
            {getSurah(surahId - 1)?.name}
          </Button>
        ) : <span />}
        {surahId < 114 && (
          <Button as={RouterLink} to={`/surahs/${surahId + 1}`} variant="outline" rightIcon={<ChevronRight size={16} />}>
            {getSurah(surahId + 1)?.name}
          </Button>
        )}
      </Flex>
    </>
  );
};


// ============ PAGE ============

const SurahPage = () => {
  const { surahId } = useParams();
  const id = Number(surahId);
  const valid = Number.isInteger(id) && id >= 1 && id <= 114;
  const player = useAyahPlayer();

  return (
    <QuranShell player={valid ? player : undefined}>
      {valid ? <SurahReader surahId={id} player={player} /> : <SurahIndex />}
    </QuranShell>
  );
};

export default SurahPage;
